import React, { useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import LogoLink from "./LogoLink";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/store";
import * as styledElements from "./HeaderStyles";
import { HideIfAboveWidth, ShowIfAboveWidth } from "../../constants/Styles";
import { HeaderHamburger, HeaderProps } from "./index";
import Icon from "../common/Icon";
import { ADMIN_GROUP, ENGINEER_GROUP } from "../../utils/group";
import {
  BoardView,
  ORDERING_STEPS,
  StepContext,
} from "../../utils/models/boardToolSteps";
import { setView } from "../../reducers/uploadedBoard";

import { DEMO_BOARD_SLUG } from "../../constants/boards";

const OrderingSteps = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const boardId = (router.query.boardId as string | undefined) || null;
  const board = useAppSelector((state) =>
    boardId ? state.uploadedBoard.boards[boardId]?.board || null : null,
  );
  const billOfMaterials = useAppSelector((state) =>
    boardId
      ? state.uploadedBoard.boards[boardId]?.billOfMaterials || null
      : null,
  );
  const view = useAppSelector((state) =>
    boardId ? state.uploadedBoard.boards[boardId]?.view || null : null,
  );
  const { groups } = useAppSelector((state) => state.auth);
  const isAdminOrEngineer =
    groups.includes(ADMIN_GROUP) || groups.includes(ENGINEER_GROUP);

  const context: StepContext = useMemo(() => {
    return {
      router,
      dispatch,
      board,
      billOfMaterials,
      view,
      isAdminOrEngineer,
    };
  }, [board, dispatch, isAdminOrEngineer, router, view, billOfMaterials]);

  const stepsToDisplay = ORDERING_STEPS.filter((step) =>
    step.shouldDisplay(context),
  );

  const boardIsLoaded = board !== null;
  const bomIsLoaded = billOfMaterials !== null;
  const queryView = router.query.view as BoardView | undefined;

  useEffect(() => {
    if (boardIsLoaded && bomIsLoaded && view === null && boardId !== null) {
      if (boardId == DEMO_BOARD_SLUG) {
        dispatch(setView({ boardId, view: BoardView.BOARD }));
      } else {
        if (
          queryView !== undefined &&
          Object.values(BoardView).includes(queryView)
        ) {
          dispatch(setView({ boardId, view: queryView }));
        } else {
          const nextStep = stepsToDisplay.find((step) => {
            return (
              !step.isCompleted(context) &&
              step.view !== BoardView.MANUFACTURING
            );
          });
          dispatch(
            setView({ boardId, view: nextStep?.view || BoardView.QUOTE }),
          );
        }
      }
    }
  }, [
    bomIsLoaded,
    boardId,
    boardIsLoaded,
    context,
    dispatch,
    queryView,
    stepsToDisplay,
    view,
  ]);

  return (
    <styledElements.OrderingSteps>
      {stepsToDisplay.map((step, index) => {
        const isSelected = step.isSelected(context);
        const isProcessing = step.isProcessing(context);
        const isCompleted = step.isCompleted(context);

        const renderIcon = () => {
          if (isCompleted) {
            return (
              <Icon
                src={"/icons/check_gray.svg"}
                alt={"checked"}
                iconDimension={20}
              />
            );
          }
          if (isProcessing) {
            return (
              <styledElements.ProcessingHeaderIcon>
                <Icon
                  src={"/icons/remove_gray.svg"}
                  alt={"updating"}
                  iconDimension={20}
                />
              </styledElements.ProcessingHeaderIcon>
            );
          }
          return (
            <Icon
              src={"/icons/remove_gray.svg"}
              alt={"updating"}
              iconDimension={20}
            />
          );
        };

        return (
          <styledElements.OrderingStep
            key={index}
            isSelected={isSelected}
            onMouseDown={(event) => step.onClick(event, context)}
          >
            <styledElements.StepMarker isPrimary={isSelected}>
              {renderIcon()}
            </styledElements.StepMarker>
            <span>{step.label}</span>
            {index !== stepsToDisplay.length - 1 && (
              <styledElements.HorizontalLine />
            )}
          </styledElements.OrderingStep>
        );
      })}
    </styledElements.OrderingSteps>
  );
};

const BOARD_HEADER_BREAKPOINT = "1100px";

export const BoardHeader = (headerProps: HeaderProps) => {
  return (
    <styledElements.Header>
      <styledElements.HeaderRow>
        <LogoLink />
        <ShowIfAboveWidth width={BOARD_HEADER_BREAKPOINT}>
          <OrderingSteps />
        </ShowIfAboveWidth>
        <HeaderHamburger {...headerProps} />
      </styledElements.HeaderRow>
      <HideIfAboveWidth width={BOARD_HEADER_BREAKPOINT}>
        <OrderingSteps />
      </HideIfAboveWidth>
    </styledElements.Header>
  );
};
